import { AppConfig } from '@ltvco/refresh-lib/ctx';
import {
  formatPostIndiFromRecord,
  AddToFamilyTreeButton,
} from '@ltvco/refresh-lib/fmt';
import { reportGetters } from '@ltvco/refresh-lib/v1';
import { Person } from '@mui/icons-material';
import { useContext } from 'react';

import { constants } from 'appConstants';
import { useSearchPersonResultsContext } from 'routes/SearchPerson/context';
import { useQueryParams } from 'utils/useQueryParams';

import { BaseCard } from '../BaseCard';
import {
  PersonIconWrapper,
  InfoSection,
  NameTypography,
  SecondaryTextTypography,
  SecondaryTextSubtitleTypography,
  CustomList,
  RelativesList,
} from '../common/components';
import { GenealogyCardData } from '../common/types';
import {
  formatName,
  excludeAgeFromName,
  getUniqueRelatives,
} from '../common/utils';

export function MobileGenealogyCard({
  name,
  birthInfo,
  locations,
  relatives,
  relativesNotSerialized,
  onSuccess,
  age,
  familySearchId,
  handleAddToFamilyTree,
  reportInTree,
  isCreatingFamilyTree,
  personData,
}: GenealogyCardData) {
  const { uniqueRelatives, uniqueRelativesIds } = getUniqueRelatives(
    relatives,
    relativesNotSerialized
  );
  const { trackEventGA4 } = useContext(AppConfig);
  const { isChatPage, handleAncestorReport } = useSearchPersonResultsContext();
  const queryParams = useQueryParams();

  const handleOpenMainGenealogyReport = async () => {
    trackEventGA4({
      event_name: 'viewed_report',
      interacted_from: 'search_results',
      report_type: 'ancestor',
    });
    onSuccess?.();
    if (familySearchId) {
      if (isChatPage) {
        queryParams.set('family_search_id', familySearchId);
        const response = await reportGetters.ancestor({ queryParams });
        const permalink = response?.report?.permalink;
        if (permalink) {
          handleAncestorReport?.(permalink);
          return;
        }
      }

      const windowObjectReference = window.open();
      // It's possible for the browser to block opening a new window, in which case windowObjectReference will be null.
      // In that case, we just redirect the current window.
      const windowTarget = windowObjectReference || window;

      windowTarget.location.href = `${constants.links.baseUrl}/report/ancestor?family_search_id=${familySearchId}`;
    }
  };

  const onHandleAddToFamilyTree = () => {
    if (!familySearchId) return;
    if (!personData) return;

    const postIndi = formatPostIndiFromRecord(personData);
    if (!postIndi) return;

    trackEventGA4({
      event_name: 'search_results_interaction',
      type: 'card click',
      action: 'fam tree cta',
      interacted_from: 'Family Search',
    });
    handleAddToFamilyTree(postIndi);
  };

  const mainLocation = locations?.[0];
  const formattedName = formatName(name || '');

  const openReport = (fsid: string) => {
    window.open(
      `${constants.links.baseUrl}/report/ancestor?family_search_id=${fsid}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const handleRelativeClick = (index: number) => {
    const relativeFsId = uniqueRelativesIds[index];
    if (!relativeFsId) return;

    trackEventGA4({
      event_name: 'search_results_interaction',
      type: 'card click',
      action: 'relative cta',
      interacted_from: 'Family Search',
    });

    openReport(relativeFsId);
  };

  return (
    <BaseCard
      name={name}
      isExpandable={true}
      onDetailsClick={handleOpenMainGenealogyReport}
      renderIcon={() => (
        <PersonIconWrapper>
          <Person color="primary" fontSize="small" />
        </PersonIconWrapper>
      )}
      renderHeader={() => (
        <>
          <NameTypography variant="subtitle1" fontWeight="bold">
            {formattedName}
          </NameTypography>
          <SecondaryTextTypography variant="body2">
            Born: {birthInfo?.trim() || '-'}
          </SecondaryTextTypography>
          {mainLocation && (
            <SecondaryTextTypography variant="body2">
              {mainLocation}
            </SecondaryTextTypography>
          )}
        </>
      )}
      renderExpandedContent={() => (
        <InfoSection>
          <SecondaryTextSubtitleTypography variant="subtitle2">
            Locations
          </SecondaryTextSubtitleTypography>
          {locations && locations.length > 0 ? (
            <CustomList items={locations as string[]} />
          ) : (
            <SecondaryTextTypography variant="body2">-</SecondaryTextTypography>
          )}

          <SecondaryTextSubtitleTypography variant="subtitle2">
            Relatives
          </SecondaryTextSubtitleTypography>
          {relatives && relatives.length > 0 ? (
            <RelativesList
              uniqueRelatives={uniqueRelatives}
              uniqueRelativesIds={uniqueRelativesIds}
              onClickHandler={handleRelativeClick}
              maxItems={5}
            />
          ) : (
            <SecondaryTextTypography variant="body2">-</SecondaryTextTypography>
          )}
          <AddToFamilyTreeButton
            variant="pill-outline"
            reportInFamilyTree={reportInTree}
            addRelativeToFamilyTree={onHandleAddToFamilyTree}
            isCreatingFamilyTree={isCreatingFamilyTree}
            sx={{ mb: 0, mt: 2.5, width: '100%' }}
          />
        </InfoSection>
      )}
      footerText={excludeAgeFromName(formattedName, age)}
      tabName="Family Search"
    />
  );
}
